// src/pages/Client/Favorites.js
// Page "Mes Favoris" - Liste des salons favoris du client

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SalonCard from '../../components/SalonCard/SalonCard';
import favoritesService from '../../services/favoritesService';
import { normalizeImageUrl } from '../../services/salonService';
import './Favorites.css';

const Favorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les favoris au montage
  useEffect(() => {
    loadFavorites();
  }, []);

  // Fonction pour charger les favoris
  const loadFavorites = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await favoritesService.getUserFavorites();
      
      // Transformer les données pour correspondre au format de SalonCard
      const formattedFavorites = data.map(fav => ({
        id: fav.salon_id,
        name: fav.name,
        description: fav.description,
        address: fav.address,
        city: fav.city,
        country: fav.country,
        phone: fav.phone,
        email: fav.email,
        cover_image: normalizeImageUrl(fav.image_url || fav.cover_image),
        image_url: normalizeImageUrl(fav.image_url || fav.cover_image),
        rating: fav.rating,
        total_reviews: fav.total_reviews,
        landmark: fav.landmark,
        status: fav.status,
        service_type: fav.service_type,
        specialties: fav.specialties,
        is_favorite: true, // Toujours true car on est dans les favoris
        created_at: fav.favorited_at, // Pour le badge "NOUVEAU"
      }));

      setFavorites(formattedFavorites);
    } catch (err) {
      console.error('❌ Erreur loadFavorites:', err);
      setError('Impossible de charger vos favoris');
    } finally {
      setLoading(false);
    }
  };

  // Callback quand un favori est retiré (appelé depuis SalonCard)
  const handleFavoriteRemoved = (salonId) => {
    setFavorites(prev => prev.filter(fav => fav.id !== salonId));
  };

  // Affichage loading
  if (loading) {
    return (
      <div className="favorites-page">
        <div className="favorites-container">
          <div className="favorites-header">
            <h1>❤️ Mes Favoris</h1>
          </div>
          <div className="favorites-loading">
            <div className="loader"></div>
            <p>Chargement de vos favoris...</p>
          </div>
        </div>
      </div>
    );
  }

  // Affichage erreur
  if (error) {
    return (
      <div className="favorites-page">
        <div className="favorites-container">
          <div className="favorites-header">
            <h1>❤️ Mes Favoris</h1>
          </div>
          <div className="favorites-error">
            <div className="error-icon">⚠️</div>
            <p>{error}</p>
            <button onClick={loadFavorites} className="retry-button">
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Affichage liste vide
  if (favorites.length === 0) {
    return (
      <div className="favorites-page">
        <div className="favorites-container">
          <div className="favorites-header">
            <h1>❤️ Mes Favoris</h1>
            <p className="favorites-subtitle">
              Vos salons préférés en un clin d'œil
            </p>
          </div>

          <div className="favorites-empty">
            <div className="empty-icon">💔</div>
            <h2>Aucun favori pour le moment</h2>
            <p>
              Explorez nos salons et ajoutez vos préférés en cliquant sur le cœur ❤️
            </p>
            <Link to="/salons" className="explore-button">
              Découvrir les salons
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Affichage liste des favoris
  return (
    <div className="favorites-page">
      <div className="favorites-container">
        {/* Header */}
        <div className="favorites-header">
          <h1>❤️ Mes Favoris</h1>
          <p className="favorites-subtitle">
            {favorites.length} salon{favorites.length > 1 ? 's' : ''} favori{favorites.length > 1 ? 's' : ''}
          </p>
        </div>

        {/* Liste des salons favoris */}
        <div className="favorites-grid">
          {favorites.map((salon) => (
            <SalonCard 
              key={salon.id} 
              salon={salon}
              onFavoriteRemoved={() => handleFavoriteRemoved(salon.id)}
            />
          ))}
        </div>

        {/* CTA pour découvrir d'autres salons */}
        <div className="favorites-footer">
          <Link to="/salons" className="discover-more">
            Découvrir d'autres salons →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Favorites;